import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Film, Loader2, SkipForward, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { hostAllowed, toEmbedSrc } from "@/lib/kbEmbeds";

/** The replace-this-video controls, floated against the video itself — the
 *  same flow as a broken picture, so a batch of dead videos can be cleared in
 *  one sitting. Paste the video's share link; we turn it into its embed form. */
export function KbEmbedFixBar({
  fixUrl, replaced, remaining, saving, anchor, onReplace, onSaveNext, onSkip, onDone,
}) {
  const [url, setUrl] = useState("");
  const [hosts, setHosts] = useState([]);

  useEffect(() => {
    api.get("/kb/settings").then((r) => setHosts(r.data.embed_hosts || [])).catch(() => {});
  }, []);

  const next = toEmbedSrc(url);
  const ok = !!next && hostAllowed(next, hosts);
  const style = anchor
    ? { top: Math.min(anchor.top + 8, window.innerHeight - 130),
        left: Math.max(12, Math.min(anchor.left, window.innerWidth - 480)) }
    : { bottom: 20, left: "50%", transform: "translateX(-50%)" };

  const panel = (
    <div data-testid="kb-embed-fix-bar" style={{ position: "fixed", zIndex: 60, ...style }}
      className={`w-[460px] max-w-[calc(100vw-24px)] rounded-xl border p-2.5 shadow-xl ${replaced
        ? "border-emerald-300 bg-emerald-50/95 dark:border-emerald-800 dark:bg-emerald-950/90"
        : "border-primary/40 bg-white/95 dark:bg-slate-900/95"}`}>
      <div className="flex items-start gap-2">
        {replaced
          ? <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
          : <Film className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />}
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">
            {replaced ? "New video in place — save to clear the alert"
              : "This video no longer plays — paste the new one"}
          </div>
          <div className="truncate text-[10px] text-slate-500 dark:text-slate-400"
            data-testid="kb-embed-fix-url" title={fixUrl}>
            {replaced ? "The dead video has been swapped out." : fixUrl}
          </div>
        </div>
        <button type="button" onClick={onDone} data-testid="kb-embed-fix-done" disabled={saving}
          className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
          title="Done for now — back to Image & link health">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {!replaced && (
        <>
          <div className="mt-2 flex items-center gap-1.5">
            <Input data-testid="kb-embed-fix-url-input" value={url} disabled={saving}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && ok) { e.preventDefault(); onReplace(next); } }}
              placeholder="Paste the video's share link or <iframe> code"
              className="h-7 bg-white text-xs dark:bg-slate-900" />
            <Button size="sm" data-testid="kb-embed-fix-use" disabled={!ok || saving}
              onClick={() => onReplace(next)} className="h-7 shrink-0 text-xs">Use this</Button>
          </div>
          {!!url.trim() && (
            <div className="mt-1 truncate text-[10px] text-slate-500 dark:text-slate-400"
              data-testid="kb-embed-fix-preview">
              {ok ? `Will embed: ${next}`
                : next ? "That host isn't on the allowed embed list — add it in KB settings first"
                  : "That doesn't look like a video link"}
            </div>
          )}
        </>
      )}

      <div className="mt-2 flex items-center gap-1.5">
        <Button size="sm" data-testid="kb-embed-fix-save-next" disabled={saving || !replaced}
          onClick={onSaveNext} className="h-7 gap-1.5 text-xs">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <SkipForward className="h-3.5 w-3.5" />}
          Save &amp; next
        </Button>
        <Button size="sm" variant="ghost" data-testid="kb-embed-fix-skip" disabled={saving}
          onClick={onSkip} className="h-7 text-xs">Skip this one</Button>
        {remaining > 0 && (
          <span data-testid="kb-embed-fix-remaining"
            className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            {remaining} left
          </span>
        )}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
